import type { Menu } from '../service/menu/types';
import type { MembershipCurrentRespVO } from '../service/membership';

// 全局状态接口
export interface GlobalState {
  menuList: Menu.Menu[];
  userInfo: any;
  isInitialized: boolean;
  /** 当前会员 + 积分快照，登录后由 InitService 预拉取，避免弹窗反复拉取导致闪烁 */
  membership: MembershipCurrentRespVO | null;
}

// 全局状态管理器
class GlobalStore {
  private state: GlobalState = {
    menuList: this.loadMenuListFromStorage(),
    userInfo: this.loadUserInfoFromStorage(),
    isInitialized: this.loadInitializedFromStorage(),
    membership: this.loadMembershipFromStorage(),
  };

  private listeners: Set<() => void> = new Set();

  // 从localStorage加载菜单列表
  private loadMenuListFromStorage(): Menu.Menu[] {
    try {
      const stored = localStorage.getItem('globalMenuList');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('加载菜单列表失败:', error);
      return [];
    }
  }

  // 从localStorage加载用户信息
  private loadUserInfoFromStorage(): any {
    try {
      const stored = localStorage.getItem('globalUserInfo');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('加载用户信息失败:', error);
      return null;
    }
  }

  // 从localStorage加载初始化状态
  private loadInitializedFromStorage(): boolean {
    try {
      const stored = localStorage.getItem('globalInitialized');
      return stored === 'true';
    } catch (error) {
      console.error('加载初始化状态失败:', error);
      return false;
    }
  }

  // 从localStorage加载会员快照（登录态下预拉取并写入）
  private loadMembershipFromStorage(): MembershipCurrentRespVO | null {
    try {
      const stored = localStorage.getItem('globalMembership');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('加载会员快照失败:', error);
      return null;
    }
  }

  // 保存菜单列表到localStorage
  private saveMenuListToStorage(menuList: Menu.Menu[]): void {
    try {
      localStorage.setItem('globalMenuList', JSON.stringify(menuList));
    } catch (error) {
      console.error('保存菜单列表失败:', error);
    }
  }

  // 保存用户信息到localStorage
  private saveUserInfoToStorage(userInfo: any): void {
    try {
      localStorage.setItem('globalUserInfo', JSON.stringify(userInfo));
    } catch (error) {
      console.error('保存用户信息失败:', error);
    }
  }

  // 保存初始化状态到localStorage
  private saveInitializedToStorage(initialized: boolean): void {
    try {
      localStorage.setItem('globalInitialized', initialized.toString());
    } catch (error) {
      console.error('保存初始化状态失败:', error);
    }
  }

  // 保存会员快照到localStorage
  private saveMembershipToStorage(membership: MembershipCurrentRespVO | null): void {
    try {
      if (membership) {
        localStorage.setItem('globalMembership', JSON.stringify(membership));
      } else {
        localStorage.removeItem('globalMembership');
      }
    } catch (error) {
      console.error('保存会员快照失败:', error);
    }
  }

  // 获取当前状态
  getState(): GlobalState {
    return { ...this.state };
  }

  // 设置菜单列表
  setMenuList(menuList: Menu.Menu[]): void {
    this.state.menuList = menuList;
    this.saveMenuListToStorage(menuList);
    this.notifyListeners();
  }

  // 设置用户信息（使用合并策略，避免覆盖关键字段如 roles、permissions）
  setUserInfo(userInfo: any): void {
    // 如果当前已有用户信息，使用合并策略保留关键字段
    if (this.state.userInfo && userInfo) {
      this.state.userInfo = {
        ...this.state.userInfo, // 先展开旧数据（包含 roles、permissions 等）
        ...userInfo              // 再覆盖新数据（比如只更新了昵称、邮箱等）
      };
    } else {
      // 如果没有现有数据，直接使用新数据
      this.state.userInfo = userInfo;
    }
    this.saveUserInfoToStorage(this.state.userInfo);
    this.notifyListeners();
  }

  // 设置初始化状态
  setInitialized(initialized: boolean): void {
    this.state.isInitialized = initialized;
    this.saveInitializedToStorage(initialized);
    this.notifyListeners();
  }

  /**
   * 写入会员快照，并通知订阅者。
   * 传 null 表示清空（用于登出/重新初始化场景）。
   */
  setMembership(membership: MembershipCurrentRespVO | null): void {
    this.state.membership = membership;
    this.saveMembershipToStorage(membership);
    this.notifyListeners();
  }

  // 清除所有数据
  clearAll(): void {
    this.state = {
      menuList: [],
      userInfo: null,
      isInitialized: false,
      membership: null,
    };
    // 清除localStorage中的数据
    try {
      localStorage.removeItem('globalMenuList');
      localStorage.removeItem('globalUserInfo');
      localStorage.removeItem('globalInitialized');
      localStorage.removeItem('globalMembership');
    } catch (error) {
      console.error('清除localStorage失败:', error);
    }
    this.notifyListeners();
  }

  // 订阅状态变化
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // 通知所有监听器
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  // 获取菜单列表
  getMenuList(): Menu.Menu[] {
    return [...this.state.menuList];
  }

  // 获取用户信息
  getUserInfo(): any {
    return this.state.userInfo;
  }

  // 获取权限列表
  getPermissions(): string[] {
    return this.state.userInfo?.permissions || [];
  }

  // 获取会员快照（登录态下由 InitService 预拉取）
  getMembership(): MembershipCurrentRespVO | null {
    return this.state.membership;
  }

  // 检查是否已初始化
  isInitialized(): boolean {
    return this.state.isInitialized;
  }
}

// 创建全局单例实例
export const globalStore = new GlobalStore();

